import React from 'react';
import {
  ChatText,
  CheckCircle,
  Clock,
  Lightning,
  NavigationArrow,
  Package,
  Play,
  X
} from '@phosphor-icons/react';

/**
 * Workflow Preview Modal - Phase 10
 * Shows workflow steps before execution with confirmation
 */
const WorkflowPreviewModal = ({ workflow, isOpen, onClose, onConfirm, loading }) => {
  if (!isOpen || !workflow) return null;

  const getStepIcon = (type) => {
    switch (type) {
      case 'navigate': return NavigationArrow;
      case 'create_session': return Lightning;
      case 'install_agent': return Package;
      case 'install_bundle': return Package;
      case 'wait': return Clock;
      case 'show_message': return ChatText;
      default: return CheckCircle;
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl z-50 px-4">
        <div className="glass rounded-2xl border border-[rgba(255,255,255,0.2)] shadow-2xl overflow-hidden max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="relative px-8 py-6 border-b border-[rgba(255,255,255,0.1)]">
            <button
              onClick={onClose}
              className="absolute top-6 right-6 text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>

            <div className="flex items-center gap-3">
              <div className="text-3xl">⚡</div>
              <div>
                <h2 className="text-xl font-bold text-white">{workflow.name}</h2>
                <p className="text-sm text-gray-400">Preview workflow steps</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-6 overflow-y-auto flex-1">
            <p className="text-gray-300 mb-6">{workflow.description}</p>

            {/* Steps List */}
            <div className="space-y-4">
              {workflow.steps?.map((step, index) => {
                const StepIcon = getStepIcon(step.type);
                
                return (
                  <div
                    key={step.step_id}
                    className="flex items-start gap-4 p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg"
                  >
                    {/* Step Number */}
                    <div className="flex-shrink-0 w-8 h-8 bg-[#6366F1] rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {index + 1}
                    </div>

                    {/* Step Icon & Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <StepIcon size={16} className="text-gray-400" />
                        <h4 className="text-white font-medium">{step.title}</h4>
                      </div>
                      <p className="text-sm text-gray-400">Type: {step.type}</p>
                      {step.requires_confirm && (
                        <span className="inline-block mt-2 text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded">
                          Requires confirmation
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Warning */}
            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-300">
                👉 This workflow will execute {workflow.steps?.length || 0} steps. You will be asked to confirm actions that require it.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-6 border-t border-[rgba(255,255,255,0.1)] flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                'Starting...'
              ) : (
                <>
                  <Play size={18} />
                  Start Workflow
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkflowPreviewModal;
